import { useState } from "react";
import {
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useMonthlySummary } from "../api/queries";
import { useRegistry } from "../context/RegistryContext";
import { CenteredSpinner } from "../components/CenteredSpinner";
import { SummaryDisplay } from "../components/SummaryDisplay";
import { SourcePickerField } from "../components/EntityPickerField";

export const MonthlySummaryPage = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [source, setSource] = useState<string>("");
  const [viewMode, setViewMode] = useState<
    "groups" | "accounts" | "categories"
  >("groups");

  const { sources, loading: isMetadataLoading } = useRegistry();

  const {
    data: summary,
    isLoading: isSummaryLoading,
    error,
  } = useMonthlySummary({
    type: "month",
    year,
    month,
    source: source || null,
  });

  const years = [];
  for (let y = 2024; y <= currentYear; y++) {
    years.push(y);
  }

  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  if (isMetadataLoading) {
    return <CenteredSpinner />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          alignItems: "center",
          mb: 4,
        }}
      >
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="year-label">Year</InputLabel>
          <Select
            labelId="year-label"
            id="year-select"
            value={year}
            label="Year"
            onChange={(e) => setYear(Number(e.target.value))}
            size="small"
            data-testid="year-select"
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="month-label">Month</InputLabel>
          <Select
            labelId="month-label"
            id="month-select"
            value={month}
            label="Month"
            onChange={(e) => setMonth(Number(e.target.value))}
            size="small"
            data-testid="month-select"
          >
            <MenuItem value={0}>
              <em>All Months</em>
            </MenuItem>
            {months.map((m) => (
              <MenuItem key={m} value={m}>
                {new Date(year, m - 1, 1).toLocaleString("default", {
                  month: "long",
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ minWidth: 200 }} data-testid="source-select">
          <SourcePickerField
            size="small"
            allowEmpty
            emptyLabel="All Sources"
            // A filter picks among what exists; it doesn't make sources.
            allowCreate={false}
            value={sources.find((s) => s.name === source)?.id ?? null}
            onChange={(_, picked) => setSource(picked?.name ?? "")}
          />
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => {
            if (newMode !== null) {
              setViewMode(newMode);
            }
          }}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="groups" aria-label="groups">
            Groups
          </ToggleButton>
          <ToggleButton value="accounts">Kort / Sparing</ToggleButton>
          <ToggleButton value="categories" aria-label="categories">
            Categories
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <SummaryDisplay
        summary={summary}
        isLoading={isSummaryLoading}
        error={error}
        viewMode={viewMode}
      />
    </Container>
  );
};
